import { useEffect, useRef, useState } from 'react';
import { Heart } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePrefersReducedMotion } from '../use-prefers-reduced-motion';
import { LottiePlayer } from './lottie-player';
import footerAnimation from './footer.json';

/**
 * The tap's beat: how long the press lasts, and therefore how long the heart is orange. ONE duration for
 * both, because the red IS the press rather than a hold timed separately from it — a tap that reddens
 * the heart and a heart that stays red for a fixed second are different gestures, and this is the
 * first. Ten fast clicks are ten beats for the same reason: each restarts this, nothing queues, and
 * the red is gone as soon as the last press resolves.
 */
const PRESS_MS = 130;

/**
 * The arm's travel, at rest and while reaching — the ONLY thing that moves in this drawing.
 *
 * The geometry forces the shape of this. The cuff is fixed and the hand may not be resized, so the
 * visible forearm — the stretch between them — can only get longer if the hand travels away from the
 * cuff. That is why the reach is a rigid translate of the hand layer and nothing else: the hand keeps
 * its exact size and slides up through a sleeve that stays put, so what grows is the arm between them.
 *
 * The sleeve in `footer.json` was rebuilt to allow it. As drawn it ended in a short tapered stub just
 * below the cuff, which the reach pulled into view as a flat cut end inside the ellipse. Its last two
 * vertices now run on to y 415 — past the 384 artboard, so clipped at every state including a full
 * reach — along the arm's own 20° lean, with both edges parallel and the width held at the cuff's.
 * The first attempt sent them down the global cuff-to-fingers axis instead, which is only 6° off
 * vertical: the left edge flattened out and kinked where it met the cuff.
 *
 * It works because of how the sleeve is assembled: the cuff ellipse sits BEHIND the arm (`ind` 5) and
 * the crease and mask in FRONT of it (`ind` 2 and 3), so the arm's lower end is painted over at both
 * the sides and the bottom. Sliding the path up pulls more arm out of that opening and hides the same
 * blunt end it always had — the same reason a real sleeve works.
 *
 * Nothing here scales. An earlier attempt grew the whole assembly from the fingertip end, which read
 * as a longer arm only because it made the hand and cuff bigger too — the drawing got larger, not the
 * arm. Values lean very slightly right because the arm is not vertical: it runs about 6° off, from the
 * cuff at (137.8, 286.1) to the fingers near (155, 129), and travelling along that axis is what keeps
 * it sliding through the opening instead of across it.
 */
const ARM_REST = '2px -8px';
const ARM_HOVER = '3px -20px';
/**
 * The press goes a little FURTHER than the hover, not back — the gesture is a poke at the heart, so
 * the finger advances into it and comes back. Six units past `ARM_HOVER` is deliberately small: the
 * fingertip is already on the heart by then, and more would push it through the shape rather than
 * against it.
 *
 * Coming back is what the state does on its own. Releasing drops `data-pressed`, the `:hover` rule is
 * still matching because the pointer is on the heart, and the arm settles to `ARM_HOVER`; if the
 * pointer has left mid-press, that rule stops matching too and it settles to `ARM_REST` instead. Both
 * exits are the absence of a state rather than a step that has to run, so the arm cannot stick — not
 * on a spam, not when the pointer leaves between the press and its release.
 *
 * Out in 120ms, back in the base 190ms. Equal durations read mechanical and a slow strike with a fast
 * recovery reads broken; a quick poke that settles is what a tap actually feels like.
 */
const ARM_PRESS = '4px -26px';

/** The wrapper's own class, and the scope of every rule below. */
const SCENE = 'new-footer-heart';

/**
 * The pressed heart is the site's PRIMARY orange, read from the token rather than pasted as a hex so
 * it follows `theme.css` if the brand is ever retuned. The fallback covers only the impossible case of
 * this stylesheet outliving the theme's.
 *
 * Not `--color-alert` (#ff6767) and not a bespoke red: the first is the FORM ERROR colour, and tying a
 * moment of affection to a token whose whole job is to say something went wrong would drag the heart
 * along the next time errors are restyled.
 */
const HEART_PRESSED = 'var(--color-brand, #ff7e1d)';

/**
 * The burst is the same primary orange, so the little hearts read as pieces of the one just pressed,
 * with `brand-strong` alternating — the palette's own darker step of the SAME hue, which keeps a
 * particle legible while it crosses the pressed heart instead of dissolving into it.
 */
const BURST_COLOURS = ['var(--color-brand, #ff7e1d)', 'var(--color-brand-strong, #f66900)'] as const;

/**
 * Where the heart sits, as a fraction of the artboard, derived from `footer.json` itself: the heart
 * layer is drawn at position [225.29, 115.38] about an anchor of [83.31, 72.72], and its path spans
 * x 2.5–151.9 / y 8.1–144.5 — so its middle lands at ~(219, 119) of the 328×384 board. Percentages
 * rather than pixels because the same box is `w-52` at `lg` and `w-64` at `xl`.
 */
const HEART_CENTRE = { x: '66.8%', y: '30.9%' };

/**
 * Burst budget. `PER_TAP` is what one click throws; `MAX_LIVE` is the ceiling a held-down spam cannot
 * push past — the oldest are dropped rather than queued, so the DOM cannot grow without bound however
 * fast the clicks arrive. Each particle also removes itself on `animationend`, which is the normal
 * path; the cap is the guarantee for when that event is delayed (a backgrounded tab throttles
 * animations, so several bursts can be in flight at once).
 */
const PER_TAP = 8;
const MAX_LIVE = 40;

type Particle = {
	id: number;
	/** Travel, and the scale it dies at — inline custom properties the keyframes read. */
	style: React.CSSProperties;
	colour: string;
	size: number;
};

/**
 * The interaction, almost entirely in CSS over the SVG lottie-web renders.
 *
 * `footer.json` tags two of its six layers with `cl` — a Lottie property the SVG renderer copies
 * straight onto the layer's `<g>` as a `class` (lottie-web `SVGBaseElement.createContainerElements`).
 * That is what makes the hand and the heart addressable without reaching into the player's internals
 * or counting anonymous groups: the artwork names them, and this stylesheet styles them.
 *
 * ONLY THE HEART IS HIT-TESTABLE. The animation's box is far wider than the drawing, so a hover on
 * the box would fire with the pointer nowhere near the heart. Everything is `pointer-events: none`
 * and the heart's group alone is `auto`, which means the target is the heart's own painted shape and
 * follows it if the artwork ever moves — no hit rectangle to keep in sync. `:hover` still reaches the
 * wrapper because hover applies to the hit target's ANCESTORS, `pointer-events: none` or not, and the
 * click handler up there only ever receives events that bubbled out of the heart.
 *
 * ONLY THE HAND LAYER MOVES — see `ARM_REST`. The cuff, its crease and its mask are untouched in every
 * state, and nothing is scaled, so the arm lengthens without the drawing growing.
 *
 * Motion uses the `translate` PROPERTY, never `transform`. Lottie writes `transform` on these same
 * elements to place them, so a CSS `transform` here would be a tug of war with the renderer; the
 * independent property composes with it instead. Lengths are SVG user units, so the reach scales with
 * the artboard rather than drifting at `xl`.
 *
 * The heart's fill is a static colour in the artwork, so lottie paints the attribute once and never
 * touches it again — a CSS `fill` outranks that presentation attribute for good.
 */
const CSS = `
.${SCENE} { pointer-events: none; }
.${SCENE} .wc-heart { pointer-events: auto; cursor: pointer; -webkit-user-select: none; user-select: none; }
.${SCENE} .wc-hand {
	translate: ${ARM_REST};
	transition: translate 190ms cubic-bezier(0.22, 0.61, 0.36, 1);
}
.${SCENE}:hover .wc-hand { translate: ${ARM_HOVER}; }
.${SCENE}[data-pressed] .wc-hand { translate: ${ARM_PRESS}; transition-duration: 120ms; }
.${SCENE} .wc-heart path { transition: fill 70ms ease-out; }
.${SCENE}[data-pressed] .wc-heart path { fill: ${HEART_PRESSED}; }
.${SCENE}-pop {
	position: absolute;
	animation: ${SCENE}-pop var(--dur) cubic-bezier(0.16, 0.72, 0.32, 1) forwards;
}
@keyframes ${SCENE}-pop {
	0% { translate: 0 0; scale: 0.3; opacity: 0; }
	18% { opacity: 1; }
	100% { translate: var(--dx) var(--dy); scale: var(--s); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
	.${SCENE}:hover .wc-hand,
	.${SCENE}[data-pressed] .wc-hand { translate: ${ARM_REST}; transition: none; }
}`;

/** One tap's worth of hearts, thrown outward on a spread of angles. */
function burst(seed: number): Particle[] {
	return Array.from({ length: PER_TAP }, (_, i) => {
		// Fanned rather than fully random: an even sweep with jitter never leaves a bald quadrant, which
		// eight uniformly random angles frequently do. Biased upward — the heart is above the hand, so
		// that is the direction the gesture is already pointing.
		const angle = -Math.PI / 2 + ((i - (PER_TAP - 1) / 2) / PER_TAP) * Math.PI * 1.6 + (Math.random() - 0.5) * 0.4;
		const distance = 42 + Math.random() * 46;
		const size = 11 + Math.round(Math.random() * 9);
		return {
			id: seed + i,
			colour: BURST_COLOURS[i % BURST_COLOURS.length]!,
			size,
			style: {
				// Centred on its own origin so it grows out of the heart rather than down-right of it.
				'marginLeft': -size / 2,
				'marginTop': -size / 2,
				'rotate': `${Math.round((Math.random() - 0.5) * 70)}deg`,
				'--dx': `${Math.round(Math.cos(angle) * distance)}px`,
				'--dy': `${Math.round(Math.sin(angle) * distance)}px`,
				'--s': (0.7 + Math.random() * 0.5).toFixed(2),
				'--dur': `${700 + Math.round(Math.random() * 400)}ms`,
			} as React.CSSProperties,
		};
	});
}

/**
 * The footer's hand-and-heart, made responsive to the reader: the arm reaches when the pointer is on
 * the heart, and a click taps it orange and throws a handful of little hearts out of it.
 *
 * Its own component rather than a flag on `LottiePlayer`, because the behaviour is this artwork's —
 * it knows which layers are the hand and the heart. The player stays generic and the site's other two
 * animations (the home stat tiles, the contact info block) are untouched by construction.
 *
 * DECORATIVE, and deliberately not focusable. The click triggers nothing, changes no state and says
 * nothing the footer does not already say in text, so putting it in the tab order would buy a
 * keyboard reader a stop whose entire reward is a colour flash and some confetti. Nothing here is
 * pointer-only INFORMATION — it is pointer-only decoration, which is the case WAI carves out. That
 * also settles the consistency question: there is no focus state and no keyboard path to diverge from
 * the pointer one, rather than a focusable box that does less than a hover.
 *
 * Under `prefers-reduced-motion` the arm does not move and no hearts are thrown — the player is
 * already showing a still last frame there — but a tap still colours the heart. (The arm keeps its
 * resting extension: that is how the drawing is posed, not something that moves.) A colour change is
 * not motion, and it leaves the gesture with something to answer. Skipping the particles in JS rather
 * than hiding them in CSS is deliberate: with animations suppressed there would be no `animationend`
 * to remove them by, so hidden particles would pile up forever.
 */
export function FooterHeart(props: { className?: string; ariaLabel?: string }) {
	const reduce = usePrefersReducedMotion();
	const [pressed, setPressed] = useState(false);
	const [particles, setParticles] = useState<Particle[]>([]);
	const press = useRef<ReturnType<typeof setTimeout>>(undefined);
	const nextId = useRef(0);

	// The footer is on every page, so a tap followed by a client-side navigation would leave this
	// firing into an unmounted component.
	useEffect(() => () => clearTimeout(press.current), []);

	const tap = (event: React.PointerEvent<HTMLDivElement>) => {
		// Only the heart is hit-testable, so every event that gets here came from it. The guard is for
		// the day something else in this box is made interactive and starts bubbling too.
		if (!(event.target as Element).closest?.('.wc-heart')) return;

		// Spamming the heart otherwise selects the footer's copy: the browser reads the repeated presses
		// as a selection drag, and a fast pair as a double-click on the text behind. Cancelling the
		// press's default stops the selection at its source; the `user-select: none` on the heart in the
		// stylesheet covers the drag that starts here and ends over the links. Both are scoped to the
		// heart alone, so the footer's own text stays selectable for anyone copying it. Nothing is lost
		// by cancelling the default here — the element is decorative and takes no focus (see above), so
		// there is no focus behaviour to restore.
		event.preventDefault();

		// The beat restarts on every tap, so a second click never races the first one's timeout into
		// cutting its press short. No lockout either: a tap is never dropped or deferred because one is
		// already running.
		clearTimeout(press.current);
		setPressed(true);
		press.current = setTimeout(() => setPressed(false), PRESS_MS);

		if (reduce) return;
		// Randomness lives in the handler, never in render: the first client render has to match the
		// server's, and on the server this list is empty.
		const seed = (nextId.current += PER_TAP);
		setParticles((live) => [...live, ...burst(seed)].slice(-MAX_LIVE));
	};

	return (
		// `pointerDown` rather than `click`: the press should answer the finger going down, not the
		// button coming back up. Both data attributes are absent rather than `false` so the CSS can
		// match on presence alone.
		//
		// The artboard's own ratio is on the WRAPPER, and it is load-bearing rather than cosmetic. The
		// player reserves its box with the class it is handed, and a bare `w-*` reserves a box 0px tall
		// — which `playWhenVisible` then observes and never sees intersect, so the animation stays a
		// placeholder forever. Sizing the wrapper gives the `h-full` child a real box to be seen in,
		// and it also stops the corner jumping from nothing to 300px when the SVG lands. Taken from the
		// animation data so it cannot drift from the file.
		<div
			className={cn(SCENE, 'relative', props.className)}
			style={{ aspectRatio: `${footerAnimation.w} / ${footerAnimation.h}` }}
			data-pressed={pressed || undefined}
			onPointerDown={tap}
			// `pointerdown`'s cancelled default suppresses the compatibility mouse events in Chrome, but
			// that is a courtesy rather than a guarantee across engines, and `mousedown` is the event whose
			// default actually starts a selection. Cancelling it too is what makes "no highlight" true
			// everywhere rather than in the browser this was tested in.
			onMouseDown={(event) => {
				if ((event.target as Element).closest?.('.wc-heart')) event.preventDefault();
			}}
		>
			{/* Hoisted and deduped by React, the way `Wheel` ships the keyframes it generates. */}
			<style href={SCENE} precedence="medium" dangerouslySetInnerHTML={{ __html: CSS }} />
			<LottiePlayer
				animationData={footerAnimation}
				loop={false}
				playWhenVisible
				ariaLabel={props.ariaLabel}
				className="h-full w-full"
			/>
			{/* Anchored on the heart, so the burst comes out of the thing that was tapped. `pointer-events`
			    is inherited from the wrapper's `none`, so these can never eat a click meant for a link
			    behind them — they are stated anyway, because that is too important to leave implicit. */}
			<span aria-hidden className="pointer-events-none absolute" style={{ left: HEART_CENTRE.x, top: HEART_CENTRE.y }}>
				{particles.map((p) => (
					<Heart
						key={p.id}
						className={`${SCENE}-pop`}
						style={{ ...p.style, width: p.size, height: p.size, color: p.colour }}
						fill="currentColor"
						stroke="none"
						// The normal way a particle leaves: it removes itself the moment its animation is over,
						// so nothing sweeps the list and no timer has to guess how long the CSS took.
						onAnimationEnd={() => setParticles((live) => live.filter((q) => q.id !== p.id))}
					/>
				))}
			</span>
		</div>
	);
}
